import { useEffect, useState } from 'react'
import { Pause, Play, Plus, RefreshCw, Users } from 'lucide-react'
import {
  EmptyRow, ErpTable, KpiStrip, MetricCard, Panel, SectionHeader, Td, Th,
} from '@/components/erp/primitives'
import { AsyncMultiSelect, FilterBar } from '@/shared/ui/filters'
import { Modal } from '@/shared/ui/Modal'
import { ErrorStrip, Forbidden, LoadingRows, Note } from '@/shared/ui/states'
import { useLoad } from '@/features/administration/hooks'
import { useCan } from '@/shared/auth/session'
import { listUnits } from '@/features/geography/api'
import { fmtDate, fmtNumber } from '@/shared/lib/format'
import { explain } from '@/shared/api/problem'
import { cn } from '@/lib/utils'
import type { MissionResponse, TaskAssignResponse } from '@/shared/api/types'
import {
  assignTasks, cancelTask, changeMissionStatus, createMission, listMissions,
  listMobilizers, listTasks,
} from './api'
import {
  MISSION_KIND, MISSION_KIND_ORDER, MISSION_STATUS_ORDER, MissionStatusChip,
  PRIORITY_ORDER, PriorityChip, TaskStatusChip, missionKindLabel, meterTone, pctValue,
  roleLabel, taskStatusLabel,
} from './vocab'
import { DatePicker } from '@/components/erp/date-picker'

/**
 * Missions and tasks.
 *
 * ## Why a mission is not a task
 *
 * A **mission** is the definition — what is to be done, over what window, with
 * what target. A **task** is one mission, one mobilizer, one unit: assigned,
 * accepted, progressed, finished. PollSphere synthesised its task list from a
 * query on every request, which is fine for a demo and wrong as a foundation:
 * nothing could be assigned, nothing accepted, and "who was supposed to do this"
 * had no answer.
 *
 * ## Assigning twice is safe
 *
 * "Give this mission to everyone in the ward" run twice does not double anybody's
 * workload — the server has a uniqueness constraint doing the work. What it
 * reports back is how many were *skipped*, and this screen shows that number
 * rather than hiding it, because "created 0" with no explanation reads as failure.
 *
 * ## Two kinds are listed and refused
 *
 * `SURVEY_COLLECTION` and `PROGRAM_ENROLMENT` exist in the vocabulary so it is
 * stable, and are refused at creation naming the phase that brings them. That is
 * better than hiding them: a coordinator planning next quarter can see the shape
 * of what is coming.
 */
export function MobilizerMissionsPage() {
  const canRead = useCan('mobilizer:read')
  const canAssign = useCan('mobilizer:assign')

  const [status, setStatus] = useState('')
  const [creating, setCreating] = useState(false)
  const [assignTo, setAssignTo] = useState<MissionResponse | null>(null)
  const [version, setVersion] = useState(0)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const missions = useLoad(
    () => (canRead ? listMissions({ status: status || undefined }) : Promise.resolve([])),
    [canRead, status, version])

  const tasks = useLoad(
    () => (canRead ? listTasks({}) : Promise.resolve([])), [canRead, version])

  if (!canRead) return <Forbidden what="missions" />

  const rows = missions.data ?? []
  const allTasks = tasks.data ?? []
  const openTasks = allTasks.filter(
    (t) => !['COMPLETED', 'CANCELLED', 'EXPIRED'].includes(t.statusCode))
  const overdue = allTasks.filter((t) => t.statusCode === 'EXPIRED')

  const cancel = async (taskId: string) => {
    setBusy(taskId); setError(null)
    try {
      await cancelTask(taskId)
      setVersion((v) => v + 1)
    } catch (err) { setError(explain(err)) } finally { setBusy(null) }
  }

  const setMissionStatus = async (m: MissionResponse, next: string) => {
    setBusy(m.id); setError(null)
    try {
      await changeMissionStatus(m.id, next)
      setVersion((v) => v + 1)
    } catch (err) { setError(explain(err)) } finally { setBusy(null) }
  }

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Missions & tasks"
        description="A mission is what needs doing. A task is one person's share of it. Progress is counted from the activity log every night — nobody types it in."
        right={(
          <div className="flex gap-1.5">
            <button type="button" className="erp-btn" aria-label="Refresh"
                    onClick={() => setVersion((v) => v + 1)}>
              <RefreshCw className="h-3.5 w-3.5" /> <span className="axis-hide-sm">Refresh</span>
            </button>
            {canAssign && (
              <button type="button" className="erp-btn-primary" onClick={() => setCreating(true)}>
                <Plus className="h-3.5 w-3.5" /> New mission
              </button>
            )}
          </div>
        )}
      />

      <KpiStrip>
        <MetricCard label="Missions" value={fmtNumber(rows.length)}
                    sub={`${rows.filter((m) => m.statusCode === 'ACTIVE').length} active`} accent />
        <MetricCard label="Open tasks" value={fmtNumber(openTasks.length)} sub="assigned and not finished" />
        <MetricCard label="Finished" value={fmtNumber(
          allTasks.filter((t) => t.statusCode === 'COMPLETED').length)}
                    sub="marked done by the person doing them" />
        <MetricCard label="Expired" value={fmtNumber(overdue.length)}
                    sub={overdue.length ? 'past due and never finished' : 'nothing lapsed'}
                    delta={overdue.length ? 'lapsed' : undefined} deltaType="down" />
      </KpiStrip>

      <FilterBar activeCount={status ? 1 : 0}>
        <select className="erp-input" value={status} aria-label="Mission status"
                onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {MISSION_STATUS_ORDER.map((s) => (
            <option key={s} value={s}>{s.charAt(0) + s.slice(1).toLowerCase()}</option>
          ))}
        </select>
      </FilterBar>

      <ErrorStrip message={error ?? missions.error} />

      <Panel bodyClassName="p-0">
        <ErpTable head={<>
          <Th>Mission</Th>
          <Th>Kind</Th>
          <Th>Window</Th>
          <Th className="text-right">Target each</Th>
          <Th>Progress</Th>
          <Th>Priority</Th>
          <Th>Status</Th>
          <Th className="w-40">Actions</Th>
        </>}>
          {missions.loading && !missions.data ? (
            <LoadingRows colSpan={8} label="Loading missions…" />
          ) : rows.length === 0 ? (
            <EmptyRow colSpan={8} message="No missions yet. A mission is how work gets assigned to more than one person at a time." />
          ) : rows.map((m) => {
            const done = pctValue(m.tasksCompleted, m.tasksTotal)
            return (
              <tr key={m.id} className="hover:bg-muted/40" data-mission={m.code}>
                <Td>
                  <span className="block font-medium truncate">{m.title}</span>
                  <span className="block text-erp-xs text-muted-foreground font-mono">{m.code}</span>
                </Td>
                <Td>{missionKindLabel(m.kindCode)}</Td>
                <Td className="whitespace-nowrap">
                  {fmtDate(m.startsOn)} → {fmtDate(m.endsOn)}
                </Td>
                <Td className="text-right erp-metric">{m.targetPerTask ?? '—'}</Td>
                <Td>
                  {m.tasksTotal === 0 ? (
                    <span className="text-erp-xs text-muted-foreground">Nobody assigned</span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <span className="h-1.5 w-16 bg-muted rounded-sm overflow-hidden shrink-0">
                        <span className={cn('block h-full', meterTone(done))}
                              style={{ width: `${done}%` }} />
                      </span>
                      <span className="erp-metric text-erp-xs">
                        {m.tasksCompleted} / {m.tasksTotal}
                      </span>
                    </span>
                  )}
                </Td>
                <Td><PriorityChip code={m.priorityCode} /></Td>
                <Td><MissionStatusChip code={m.statusCode} /></Td>
                <Td>
                  {canAssign && (
                    <span className="flex flex-wrap gap-1.5">
                      {m.statusCode === 'DRAFT' && (
                        <button type="button" className="erp-btn" disabled={busy === m.id}
                                onClick={() => void setMissionStatus(m, 'ACTIVE')}>
                          <Play className="h-3.5 w-3.5" /> Activate
                        </button>
                      )}
                      {m.statusCode === 'ACTIVE' && (
                        <>
                          <button type="button" className="erp-btn" onClick={() => setAssignTo(m)}>
                            <Users className="h-3.5 w-3.5" /> Assign
                          </button>
                          <button type="button" className="erp-btn" disabled={busy === m.id}
                                  aria-label={`Pause ${m.title}`}
                                  onClick={() => void setMissionStatus(m, 'PAUSED')}>
                            <Pause className="h-3.5 w-3.5" />
                          </button>
                        </>
                      )}
                      {m.statusCode === 'PAUSED' && (
                        <button type="button" className="erp-btn" disabled={busy === m.id}
                                onClick={() => void setMissionStatus(m, 'ACTIVE')}>
                          <Play className="h-3.5 w-3.5" /> Resume
                        </button>
                      )}
                    </span>
                  )}
                </Td>
              </tr>
            )
          })}
        </ErpTable>
      </Panel>

      <Panel title="Task states"
             description="Across every mission. A task cannot skip a state — the server keeps the transition table, and the buttons come from it.">
        <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-6">
          {(['ASSIGNED', 'ACCEPTED', 'IN_PROGRESS', 'COMPLETED', 'EXPIRED', 'CANCELLED'] as const)
            .map((s) => (
              <div key={s} className="erp-card px-3 py-2">
                <div className="text-erp-xs text-muted-foreground truncate">{taskStatusLabel(s)}</div>
                <div className="erp-metric text-erp-lg font-semibold">
                  {allTasks.filter((t) => t.statusCode === s).length}
                </div>
              </div>
            ))}
        </div>
      </Panel>

      {canAssign && openTasks.length > 0 && (
        <Panel
          bodyClassName="p-0"
          title={`Open tasks (${openTasks.length})`}
          description="Cancelling is a coordinator's act — the person doing the work finishes theirs, they do not cancel it."
        >
          <ErpTable head={<>
            <Th>Task</Th>
            <Th>Assigned to</Th>
            <Th>Unit</Th>
            <Th>Status</Th>
            <Th className="text-right">Progress</Th>
            <Th>Due</Th>
            <Th className="w-24">Actions</Th>
          </>}>
            {openTasks.map((t) => (
              <tr key={t.id} className="hover:bg-muted/40" data-task={t.id}>
                <Td><span className="truncate">{t.missionTitle ?? t.missionCode}</span></Td>
                <Td>{t.mobilizerName ?? '—'}</Td>
                <Td>{t.unitName ?? '—'}</Td>
                <Td><TaskStatusChip code={t.statusCode} /></Td>
                <Td className="text-right erp-metric">
                  {t.progressCount}{t.targetCount ? ` / ${t.targetCount}` : ''}
                </Td>
                <Td>{t.dueOn ? fmtDate(t.dueOn) : '—'}</Td>
                <Td>
                  {/* Driven by the server's own transition table, not a copy of it
                      kept here — a second copy is one release away from offering a
                      button the server refuses. */}
                  {t.allowedTransitions.includes('CANCELLED') && (
                    <button type="button" className="erp-btn" disabled={busy === t.id}
                            onClick={() => void cancel(t.id)}>
                      Cancel
                    </button>
                  )}
                </Td>
              </tr>
            ))}
          </ErpTable>
        </Panel>
      )}

      <CreateMissionDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={() => { setCreating(false); setVersion((v) => v + 1) }}
      />

      <AssignTasksDialog
        mission={assignTo}
        onClose={() => setAssignTo(null)}
        onDone={() => { setAssignTo(null); setVersion((v) => v + 1) }}
      />
    </div>
  )
}

function CreateMissionDialog({
  open, onClose, onCreated,
}: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const today = new Date().toISOString().slice(0, 10)
  const [code, setCode] = useState('')
  const [title, setTitle] = useState('')
  const [kind, setKind] = useState('VERIFY_VOTERS')
  const [priority, setPriority] = useState('MEDIUM')
  const [startsOn, setStartsOn] = useState(today)
  const [endsOn, setEndsOn] = useState('')
  const [target, setTarget] = useState('')
  const [scope, setScope] = useState<{ value: string; label: string }[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Cancelling half-way through and reopening should give a blank form, not
  // yesterday's abandoned draft. `save()` clears some fields; this clears all of
  // them, in the one place that runs on every open.
  useEffect(() => {
    if (!open) return
    setCode(''); setTitle(''); setKind('VERIFY_VOTERS'); setPriority('MEDIUM')
    setStartsOn(today); setEndsOn(''); setTarget(''); setScope([]); setError(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const unavailable = MISSION_KIND[kind as keyof typeof MISSION_KIND]?.available === false
  const valid = code.trim() && title.trim() && startsOn && endsOn && endsOn >= startsOn && !unavailable

  const save = async () => {
    setBusy(true); setError(null)
    try {
      await createMission({
        code: code.trim().toUpperCase(),
        title: title.trim(),
        kind,
        priority,
        startsOn,
        endsOn,
        targetPerTask: target ? Number(target) : null,
        scopeUnitId: scope[0]?.value ?? null,
        parameters: {},
      })
      setCode(''); setTitle(''); setEndsOn(''); setTarget(''); setScope([])
      onCreated()
    } catch (err) { setError(explain(err)) } finally { setBusy(false) }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      width="lg"
      title="New mission"
      description="It is created as a draft. Assigning tasks needs an active mission, so activating is a separate, deliberate step."
      footer={(
        <>
          <button type="button" className="erp-btn" onClick={onClose}>Cancel</button>
          <button type="button" className="erp-btn-primary" disabled={busy || !valid}
                  onClick={() => void save()}>
            {busy ? 'Creating…' : 'Create draft'}
          </button>
        </>
      )}
    >
      <div className="space-y-3">
        <ErrorStrip message={error} />
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="block text-erp-xs text-muted-foreground mb-1">Code</span>
            <input className="erp-input w-full font-mono" value={code}
                   onChange={(e) => setCode(e.target.value)} placeholder="VERIFY-Q3" />
          </label>
          <label className="block">
            <span className="block text-erp-xs text-muted-foreground mb-1">Title</span>
            <input className="erp-input w-full" value={title}
                   onChange={(e) => setTitle(e.target.value)}
                   placeholder="Verify the ward register before the deadline" />
          </label>
          <label className="block">
            <span className="block text-erp-xs text-muted-foreground mb-1">Kind</span>
            <select className="erp-input w-full" value={kind}
                    onChange={(e) => setKind(e.target.value)}>
              {MISSION_KIND_ORDER.map((k) => (
                <option key={k} value={k}>
                  {MISSION_KIND[k].label}{MISSION_KIND[k].available ? '' : ' — not yet'}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="block text-erp-xs text-muted-foreground mb-1">Priority</span>
            <select className="erp-input w-full" value={priority}
                    onChange={(e) => setPriority(e.target.value)}>
              {PRIORITY_ORDER.map((p) => (
                <option key={p} value={p}>{p.charAt(0) + p.slice(1).toLowerCase()}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="block text-erp-xs text-muted-foreground mb-1">Starts</span>
            <DatePicker value={startsOn} onChange={setStartsOn} className="w-full" />
          </label>
          <label className="block">
            <span className="block text-erp-xs text-muted-foreground mb-1">Ends</span>
            <DatePicker value={endsOn} onChange={setEndsOn} min={startsOn} className="w-full" />
          </label>
          <label className="block">
            <span className="block text-erp-xs text-muted-foreground mb-1">
              Target for each person
            </span>
            <input className="erp-input w-full" type="number" min={1} value={target}
                   onChange={(e) => setTarget(e.target.value)} placeholder="20" />
          </label>
        </div>

        <label className="block">
          <span className="block text-erp-xs text-muted-foreground mb-1">
            Limit to a unit (optional)
          </span>
          <AsyncMultiSelect
            label="Scope"
            selected={scope}
            onChange={(next) => setScope(next.slice(-1))}
            placeholder="Search a county, constituency or ward"
            search={async (term) => {
              const found = await listUnits({ q: term } as never, 0, 10)
              return found.content.map((u) => ({ value: u.id, label: u.name, sub: u.code }))
            }}
          />
        </label>

        {unavailable && (
          <Note tone="info">
            {MISSION_KIND[kind as keyof typeof MISSION_KIND]?.note}. The kind is defined so
            the vocabulary does not change later, but the server will refuse it until the
            feature it depends on exists.
          </Note>
        )}
      </div>
    </Modal>
  )
}

function AssignTasksDialog({
  mission, onClose, onDone,
}: { mission: MissionResponse | null; onClose: () => void; onDone: () => void }) {
  const [people, setPeople] = useState<{ value: string; label: string }[]>([])
  const [dueOn, setDueOn] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<TaskAssignResponse | null>(null)

  const run = async () => {
    if (!mission) return
    setBusy(true); setError(null)
    try {
      setResult(await assignTasks(mission.id, {
        mobilizerIds: people.map((p) => p.value),
        dueOn: dueOn || null,
        targetCount: mission.targetPerTask,
      }))
    } catch (err) { setError(explain(err)) } finally { setBusy(false) }
  }

  const close = () => { setPeople([]); setDueOn(''); setResult(null); setError(null); onClose() }

  return (
    <Modal
      open={mission !== null}
      onClose={close}
      title={mission ? `Assign “${mission.title}”` : 'Assign'}
      description="Running this twice is safe — anyone who already has this mission is skipped, and the count says how many."
      footer={result ? (
        <button type="button" className="erp-btn-primary" onClick={() => { close(); onDone() }}>
          Done
        </button>
      ) : (
        <>
          <button type="button" className="erp-btn" onClick={close}>Cancel</button>
          <button type="button" className="erp-btn-primary" disabled={busy || people.length === 0}
                  onClick={() => void run()}>
            {busy ? 'Assigning…' : `Assign to ${people.length || ''}`}
          </button>
        </>
      )}
    >
      <div className="space-y-3">
        <ErrorStrip message={error} />
        {result ? (
          <Note tone="good">
            {result.created} task{result.created === 1 ? '' : 's'} created
            {result.alreadyAssigned > 0
              ? `, ${result.alreadyAssigned} skipped because they already had this mission.`
              : '.'}
          </Note>
        ) : (
          <>
            <AsyncMultiSelect
              label="Mobilizers"
              selected={people}
              onChange={setPeople}
              placeholder="Search by name"
              search={async (term) => {
                const page = await listMobilizers({ q: term, status: 'ACTIVE' }, undefined, 25)
                return page.content.map((m) => ({
                  value: m.id, label: m.fullName,
                  sub: `${roleLabel(m.roleCode)} · ${m.primaryUnitName ?? 'no unit'}`,
                }))
              }}
            />
            <label className="block">
              <span className="block text-erp-xs text-muted-foreground mb-1">
                Due date (defaults to the mission's end)
              </span>
              <DatePicker value={dueOn} onChange={setDueOn} className="w-full" />
            </label>
            <p className="text-erp-xs text-muted-foreground">
              Each person gets a task at their own home unit, with the mission's target and
              its priority copied onto it. Raising the mission's priority later will not
              re-rank a task somebody has already accepted — what they were told is a fact
              about the past.
            </p>
          </>
        )}
      </div>
    </Modal>
  )
}

/** Re-exported for the tasks board so both screens agree on the status vocabulary. */
export { TaskStatusChip }
